/**
 * The PLAN §12 event taxonomy and the typed emit helper every call site
 * routes through. `track()` no-ops until `initAnalytics()` (posthog.ts) has
 * run with a real key — callers never need to check that themselves, and a
 * missing/invalid key degrades to zero PostHog calls rather than an error.
 *
 * Property shapes never carry the item stem/options/explanation text, and
 * never an email/name — see the dev-mode PII guard below.
 */
import posthog from 'posthog-js';
import { isAnalyticsInitialized } from './posthog';

export interface AnalyticsEventMap {
  signup: { method: 'email' | 'google' };
  guest_started: Record<string, never>;
  activation_first_session_completed: { session_id: string };
  session_started: { ecosystem: string; route: string };
  session_completed: { session_id: string; items_answered: number };
  item_answered: {
    skill_key: string;
    domain_key: string;
    correct: boolean;
    xp: number;
    response_ms: number;
  };
  warmup_completed: { session_id: string };
  boss_attempted: { boss_key: string };
  boss_passed: { boss_key: string };
  level_skipped: { level_key: string };
  skip_preview_viewed: { ecosystem: string; route_key: string; module_id: string };
  streak_extended: { streak_days: number };
  streak_frozen: { streak_days: number };
  streak_lost: { streak_days: number };
  daily_challenge_answered: { correct: boolean };
  share_created: { kind: string };
  share_clicked: { kind: string };
  paywall_viewed: { surface: string };
  purchase_completed: { plan: string };
  refund: { plan: string };
  exam_date_set: { days_out: number };
  training_time_set: { hour: number; weekdays: number[] };
  exam_outcome_reported: { passed: boolean };
  push_permission_granted: Record<string, never>;
  deadline_stance_set: { stance: 'has_date' | 'no_date' };
  study_intent_set: { new_atoms_per_day: number };
  goal_updated: { deadline_stance: 'has_date' | 'no_date' | null };
}

export type AnalyticsEvent = keyof AnalyticsEventMap;

const PII_PROPERTY_KEY = /email|name|password|token/i;

export function track<E extends AnalyticsEvent>(
  event: E,
  properties: AnalyticsEventMap[E],
): void {
  if (process.env.NODE_ENV !== 'production') {
    for (const key of Object.keys(properties)) {
      if (PII_PROPERTY_KEY.test(key)) {
        throw new Error(
          `analytics: property "${key}" on "${event}" looks like PII — rename or drop it.`,
        );
      }
    }
  }
  if (!isAnalyticsInitialized()) return;
  posthog.capture(event, properties);
}
